import { GAME_CONFIG, LevelConfig } from '../config.ts';
import { Player } from '../entities/Player.ts';
import { Item } from '../entities/Item.ts';
import { ItemFactory } from '../entities/ItemFactory.ts';
import { InputSystem } from '../systems/InputSystem.ts';
import { CollisionSystem } from '../systems/Collision.ts';
import { ParticleSystem } from '../systems/ParticleSystem.ts';
import { RenderSystem } from '../systems/RenderSystem.ts';
import { AudioSystem } from '../systems/AudioSystem.ts';
import { UIManager } from '../ui/UIManager.ts';
import { StorageService, Language } from './Storage.ts';
import { getTranslation, getLevelTranslation } from '../i18n.ts';
import {
  trackScreenView,
  trackGameStart,
  trackLevelUp,
  trackGameEnd,
  trackGirlSelect,
  trackSettingsChange,
  setAnalyticsConsent,
  isAnalyticsActiveInUI,
} from './Analytics.ts';

export type GameState = 'MENU' | 'PLAYING' | 'PAUSED' | 'GAMEOVER';

export class Game {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private state: GameState = 'MENU';
  private dpr: number = 1;

  // Game Entities & Systems
  private player: Player;
  private items: Item[] = [];
  private itemFactory: ItemFactory;
  private inputSystem: InputSystem;
  private particleSystem: ParticleSystem;
  private renderSystem: RenderSystem;
  private audioSystem: AudioSystem;
  private uiManager: UIManager;

  // Game State Variables
  private score: number = 0;
  private highScore: number = 0;
  private lives: number = GAME_CONFIG.initialLives;
  private currentLevelIndex: number = 0;
  private spawnTimer: number = 0;
  private lastTime: number = 0;
  private currentLang: Language;

  // Combo system
  private comboCount: number = 0;
  private comboTimer: number = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;

    this.currentLang = StorageService.getLanguage();

    this.player = new Player(GAME_CONFIG.virtualWidth, GAME_CONFIG.virtualHeight);
    this.player.setLanguage(this.currentLang);

    if (!document.getElementById('models-chips-container')) {
      StorageService.setSelectedGirl('random');
    }
    const selectedGirl = StorageService.getSelectedGirl();
    this.itemFactory = new ItemFactory(GAME_CONFIG.virtualWidth, selectedGirl);
    this.inputSystem = new InputSystem(this.canvas, GAME_CONFIG.virtualWidth);
    this.particleSystem = new ParticleSystem();
    this.renderSystem = new RenderSystem(GAME_CONFIG.virtualWidth, GAME_CONFIG.virtualHeight);
    this.audioSystem = new AudioSystem();
    this.uiManager = new UIManager();

    this.uiManager.applyLanguage(this.currentLang);
    this.uiManager.setSelectedGirl(selectedGirl);
    this.uiManager.setAnalyticsEnabled(isAnalyticsActiveInUI());
    this.uiManager.onLanguageSelect = (lang: Language) => this.setLanguage(lang);
    this.uiManager.onGirlSelect = (modelId: string) => {
      StorageService.setSelectedGirl(modelId);
      this.itemFactory.setSelectedGirl(modelId);
      this.audioSystem.unlock();
      this.audioSystem.play('click');
      if (this.state === 'MENU') {
        this.audioSystem.startMenuMusic();
      }
      trackGirlSelect(modelId);
    };
    this.uiManager.onAnalyticsToggle = (enabled: boolean) => {
      this.audioSystem.unlock();
      this.audioSystem.play('click');
      if (this.state === 'MENU') {
        this.audioSystem.startMenuMusic();
      }
      setAnalyticsConsent(enabled);
      trackSettingsChange('analytics', enabled);
    };
    this.uiManager.onRulesOpen = () => {
      this.audioSystem.unlock();
      this.audioSystem.play('click');
      if (this.state === 'MENU') {
        this.audioSystem.startMenuMusic();
      }
      trackScreenView('rules');
    };
    this.uiManager.onScoreOpen = () => {
      this.audioSystem.unlock();
      this.audioSystem.play('click');
      if (this.state === 'MENU') {
        this.audioSystem.startMenuMusic();
      }
      trackScreenView('score');
    };

    this.setupResolution();
    window.addEventListener('resize', () => this.setupResolution());

    this.highScore = StorageService.getHighScore();

    this.setupListeners();
    this.init();
  }

  public setLanguage(lang: Language): void {
    this.currentLang = lang;
    StorageService.setLanguage(lang);
    this.player.setLanguage(lang);
    this.uiManager.applyLanguage(lang);
    trackSettingsChange('language', lang);

    if (this.state === 'MENU') {
      this.audioSystem.unlock();
      this.audioSystem.startMenuMusic();
    } else if (this.state === 'PLAYING' || this.state === 'PAUSED') {
      const level = this.getCurrentLevel();
      const levelInfo = getLevelTranslation(level.id, this.currentLang);
      const activeMultiplier = this.comboCount > 1 ? this.comboCount : 1;
      this.uiManager.updateHUD(this.score, this.highScore, this.lives, level, activeMultiplier, levelInfo.name);
    }
  }

  private setupResolution(): void {
    const rect = this.canvas.getBoundingClientRect();
    const aspect = rect.height > 0 && rect.width > 0 ? rect.height / rect.width : 16 / 9;

    // Dynamically adjust virtualHeight to match the display aspect ratio perfectly (prevents oval stretching)
    const dynamicVirtualHeight = Math.round(GAME_CONFIG.virtualWidth * aspect);
    GAME_CONFIG.virtualHeight = Math.max(750, Math.min(1050, dynamicVirtualHeight));

    this.dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.round(GAME_CONFIG.virtualWidth * this.dpr);
    this.canvas.height = Math.round(GAME_CONFIG.virtualHeight * this.dpr);

    if (this.player) {
      this.player.y = GAME_CONFIG.virtualHeight - GAME_CONFIG.trayYOffset;
    }
    if (this.renderSystem) {
      this.renderSystem.resize(GAME_CONFIG.virtualWidth, GAME_CONFIG.virtualHeight);
    }
  }

  private setupListeners(): void {
    this.inputSystem.onEscape = () => {
      if (this.uiManager.isRulesOpen()) {
        this.uiManager.closeRules();
      } else if (this.state === 'PLAYING') {
        this.pauseGame();
      } else if (this.state === 'PAUSED') {
        this.resumeGame();
      } else if (this.state === 'GAMEOVER') {
        this.showMenu();
      }
    };

    this.inputSystem.onPauseToggle = () => {
      if (this.state === 'PLAYING') {
        this.pauseGame();
      } else if (this.state === 'PAUSED') {
        this.resumeGame();
      }
    };

    this.inputSystem.onMuteToggle = () => {
      this.toggleMute();
    };

    this.inputSystem.onRestart = () => {
      if (this.state === 'GAMEOVER') {
        this.startGame();
      }
    };

    // Auto-pause when user minimizes or switches tabs
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        if (this.state === 'PLAYING') {
          this.pauseGame();
        } else {
          this.audioSystem.pauseAudio();
        }
      } else {
        if (this.state !== 'PAUSED') {
          this.audioSystem.resumeAudio();
        }
      }
    });
  }

  private init(): void {
    this.showMenu();
    requestAnimationFrame((t) => this.gameLoop(t));
  }

  public showMenu(): void {
    this.state = 'MENU';
    trackScreenView('menu');
    this.audioSystem.startMenuMusic();
    this.items = [];
    this.uiManager.showMenu(
      this.highScore,
      () => this.startGame(),
      () => this.toggleMute(),
      this.audioSystem.getMuted()
    );
  }

  public toggleMute(): void {
    const isMuted = this.audioSystem.toggleMute();
    this.uiManager.updateMuteIcon(isMuted);
    trackSettingsChange('sound_muted', isMuted);
    if (!isMuted && this.state === 'MENU') {
      this.audioSystem.unlock();
      this.audioSystem.startMenuMusic();
    }
  }

  public startGame(): void {
    this.audioSystem.unlock();
    this.audioSystem.play('click');

    this.state = 'PLAYING';
    trackGameStart({
      selectedGirl: StorageService.getSelectedGirl(),
      highScore: this.highScore,
    });
    trackScreenView('playing');

    this.score = 0;
    this.lives = GAME_CONFIG.initialLives;
    this.currentLevelIndex = 0;
    this.items = [];
    this.spawnTimer = 0;
    this.comboCount = 0;
    this.comboTimer = 0;

    this.player.reset(GAME_CONFIG.virtualWidth, GAME_CONFIG.virtualHeight);
    this.particleSystem.clear();
    this.inputSystem.reset();

    const level = this.getCurrentLevel();
    const levelInfo = getLevelTranslation(level.id, this.currentLang);
    this.audioSystem.setLevelBpm(105);
    this.audioSystem.startMusic();

    this.uiManager.showHUD(() => this.pauseGame());
    this.uiManager.updateHUD(this.score, this.highScore, this.lives, level, 1, levelInfo.name);
  }

  public pauseGame(): void {
    if (this.state !== 'PLAYING') return;
    this.state = 'PAUSED';
    trackScreenView('pause');
    this.audioSystem.pauseAudio();
    this.uiManager.showPause(
      () => this.resumeGame(),
      () => this.showMenu()
    );
  }

  public resumeGame(): void {
    if (this.state !== 'PAUSED') return;
    this.state = 'PLAYING';
    trackScreenView('playing');
    this.audioSystem.resumeAudio();
    this.uiManager.hidePause();
  }

  public getCurrentLevel(): LevelConfig {
    const levels = GAME_CONFIG.levels;
    for (let i = levels.length - 1; i >= 0; i--) {
      if (this.score >= levels[i].minScore) {
        return levels[i];
      }
    }
    return levels[0];
  }

  private addScore(points: number): void {
    const previousLevelIndex = this.currentLevelIndex;
    this.score += points;

    const levels = GAME_CONFIG.levels;
    for (let i = levels.length - 1; i >= 0; i--) {
      if (this.score >= levels[i].minScore) {
        if (i !== previousLevelIndex) {
          this.onLevelUp(i);
        }
        break;
      }
    }
  }

  private onLevelUp(newIndex: number): void {
    this.currentLevelIndex = newIndex;
    const level = GAME_CONFIG.levels[newIndex];
    const levelInfo = getLevelTranslation(level.id, this.currentLang);

    trackLevelUp({
      levelId: level.id,
      levelTheme: level.bgTheme,
      score: this.score,
    });

    const bpms = [105, 124, 142];
    this.audioSystem.setLevelBpm(bpms[newIndex] || 140);
    this.audioSystem.play('levelup');

    this.renderSystem.triggerLevelBanner(
      `🔥 ${levelInfo.name}!`,
      levelInfo.bannerSub,
      level.accentColor
    );

    this.particleSystem.addSparks(
      GAME_CONFIG.virtualWidth / 2,
      GAME_CONFIG.virtualHeight * 0.4,
      40,
      ['#ff007f', '#00f0ff', '#ffd700', '#ffffff']
    );
  }

  private gameLoop(timestamp: number): void {
    if (this.lastTime === 0) {
      this.lastTime = timestamp;
    }
    const dt = Math.min((timestamp - this.lastTime) / 1000, 0.1);
    this.lastTime = timestamp;

    this.update(dt);
    this.render();

    requestAnimationFrame((t) => this.gameLoop(t));
  }

  private update(dt: number): void {
    this.renderSystem.update(dt);
    this.particleSystem.update(dt);

    if (this.state !== 'PLAYING') return;

    if (this.comboCount > 0) {
      this.comboTimer -= dt;
      if (this.comboTimer <= 0) {
        this.comboCount = 0;
        const level = this.getCurrentLevel();
        const levelInfo = getLevelTranslation(level.id, this.currentLang);
        this.uiManager.updateHUD(this.score, this.highScore, this.lives, level, 1, levelInfo.name);
      }
    }

    this.player.update(
      dt,
      this.inputSystem.targetX,
      this.inputSystem.moveLeft,
      this.inputSystem.moveRight
    );

    const level = this.getCurrentLevel();

    this.spawnTimer += dt * 1000;
    if (this.spawnTimer >= level.spawnInterval) {
      this.spawnTimer = 0;
      this.items.push(this.itemFactory.spawn(level, this.score));
    }

    for (let i = this.items.length - 1; i >= 0; i--) {
      const item = this.items[i];
      item.update(dt);

      if (CollisionSystem.checkCatch(this.player, item)) {
        item.isCaught = true;
        this.handleItemCatch(item);
        this.items.splice(i, 1);
        continue;
      }

      if (item.y > GAME_CONFIG.virtualHeight + 55) {
        if (item.type === 'girl') {
          this.particleSystem.addText(item.x, GAME_CONFIG.virtualHeight - 35, '💔', '#ff6699');
        }
        this.items.splice(i, 1);
      }
    }
  }

  private handleItemCatch(item: Item): void {
    switch (item.type) {
      case 'girl': {
        this.comboCount = Math.min(GAME_CONFIG.maxCombo, this.comboCount + 1);
        this.comboTimer = GAME_CONFIG.comboTimeWindow;

        const multiplier = this.comboCount > 1 ? this.comboCount : 1;
        const earnedPoints = 10 * multiplier;
        this.addScore(earnedPoints);

        // Play the alternating girl voice moan on every girl catch
        this.audioSystem.play('girl');

        if (multiplier > 1) {
          this.audioSystem.play('combo');
          this.particleSystem.addText(item.x, item.y - 15, `x${multiplier} COMBO! +${earnedPoints}`, '#ffd700');
          this.particleSystem.addSparks(item.x, item.y, 22, ['#ffd700', '#ff007f', '#ffffff']);
        } else {
          this.particleSystem.addText(item.x, item.y - 15, '+10', '#ff007f');
          this.particleSystem.addSparks(item.x, item.y, 16, ['#ff007f', '#ffffff', '#ff66b2']);
        }
        break;
      }

      case 'penis': {
        this.comboCount = 0;
        this.lives -= 1;
        this.audioSystem.play('penis');
        this.renderSystem.triggerScreenShake(0.3, 8);
        this.particleSystem.addText(item.x, item.y - 15, '-1 ❤️', '#00f0ff');
        this.particleSystem.addSparks(item.x, item.y, 14, ['#00f0ff', '#ff0055']);
        break;
      }

      case 'grenade': {
        this.comboCount = 0;
        this.lives -= 1;
        this.audioSystem.play('grenade');
        this.renderSystem.triggerScreenShake(GAME_CONFIG.shakeDuration, GAME_CONFIG.shakeIntensity);
        const boomText = getTranslation(this.currentLang).floating.boom;
        this.particleSystem.addText(item.x, item.y - 20, boomText, '#ff3300');
        this.particleSystem.addSparks(item.x, item.y, 30, ['#ffcc00', '#ff3300', '#ffffff', '#333333']);
        break;
      }

      case 'monkey': {
        this.addScore(5);
        this.player.activateBoost(GAME_CONFIG.boostDuration);
        this.audioSystem.play('monkey');
        const turboText = getTranslation(this.currentLang).floating.turbo;
        this.particleSystem.addText(item.x, item.y - 15, turboText, '#ffd700');
        this.particleSystem.addSparks(item.x, item.y, 20, ['#ffd700', '#ffffff', '#ff9900']);
        break;
      }

      case 'dog': {
        this.addScore(5);
        this.audioSystem.play('dog');
        this.particleSystem.addText(item.x, item.y - 15, '🐶 +5', '#00ffcc');
        this.particleSystem.addSparks(item.x, item.y, 14, ['#00ffcc', '#ffffff', '#0099ff']);
        break;
      }
    }

    const level = this.getCurrentLevel();
    const levelInfo = getLevelTranslation(level.id, this.currentLang);
    const activeMultiplier = this.comboCount > 1 ? this.comboCount : 1;
    this.uiManager.updateHUD(this.score, this.highScore, this.lives, level, activeMultiplier, levelInfo.name);

    if (this.lives <= 0) {
      this.triggerGameOver();
    }
  }

  private triggerGameOver(): void {
    this.state = 'GAMEOVER';
    this.audioSystem.stopMusic();
    this.audioSystem.play('gameover');

    const isNewRecord = StorageService.setHighScore(this.score);
    if (isNewRecord) {
      this.highScore = this.score;
    }

    const level = this.getCurrentLevel();
    const levelInfo = getLevelTranslation(level.id, this.currentLang);

    trackGameEnd({
      score: this.score,
      highScore: this.highScore,
      isNewRecord,
      levelReached: level.id,
      levelTheme: level.bgTheme,
      selectedGirl: StorageService.getSelectedGirl(),
    });
    trackScreenView('gameover');

    this.uiManager.showGameOver(
      this.score,
      levelInfo.name,
      this.highScore,
      isNewRecord,
      () => this.startGame(),
      () => this.showMenu()
    );
  }

  private render(): void {
    this.ctx.save();

    this.ctx.scale(this.dpr, this.dpr);
    this.ctx.clearRect(0, 0, GAME_CONFIG.virtualWidth, GAME_CONFIG.virtualHeight);

    this.renderSystem.applyCamera(this.ctx);

    const level = this.getCurrentLevel();
    this.renderSystem.drawBackground(this.ctx, level);
    this.renderSystem.drawLevelBanner(this.ctx);

    for (const item of this.items) {
      item.draw(this.ctx);
    }

    if (this.state === 'PLAYING' || this.state === 'PAUSED') {
      this.player.draw(this.ctx);
    }

    this.particleSystem.draw(this.ctx);

    this.ctx.restore();
  }
}
